import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import moment from "moment/moment";
import Box from "@mui/material/Box";
import {useAuth} from "../../../../hooks/useAuth";
import useDataTable from "../../../../hooks/useDataTable";
import GenericButton from "../../../../shared/ui/GenericButton";
import CustomDataTable from "../../../../shared/tables/CustomDataTable";
import {APIGetCustomItems} from "../api/customItems";
import {APIArchiveCustomItem} from "../api/customItem";
import {APIPostCustomItemsSubjects} from "../api/customSubcategories";
import {TableColumnsWithFilter} from "../../../../shared/tables/_interfaces";

export interface IItem {
    id: number;
    title: string;
    subject: string;
    grade: string;
    subcategory_name: string;
    created_at: string;
}

export const ACTIVE_ITEMS_TEACHER: TableColumnsWithFilter[] = [
    {id: "title", label: "Titlu", noWrap: true, sortable: true, width: "200px", tooltipTextLength: 21, tooltip: true,},
    {id: "subject", label: "Disciplină", noWrap: true, sortable: true, width: "130px", filter: true, filterType: "select"},
    {id: "grade", label: "An", noWrap: true, sortable: true, width: "40px"},
    {id: "subcategory", label: "Subcategorie", noWrap: true, tooltip: true, width: "200px", tooltipTextLength: 21,},
    {id: "created_at", label: "Dată", noWrap: true, sortable: true, width: "90px", filter: true, filterType: "dateRange",},
    {id: "id", label: "ID", noWrap: true, sortable: true, numeric: true, width: "70px"},
    {id: "actions", label: "Acțiuni", numeric: true, sticky: true, width: "80px"},
];

export const ACTIVE_ITEMS_PRINCIPAL:  TableColumnsWithFilter[] = [
    {id: "title", label: "Titlu", noWrap: true, sortable: true, width: "200px", tooltipTextLength: 21, tooltip: true,},
    {id: "subject", label: "Disciplină", noWrap: true, sortable: true, width: "130px", filter: true, filterType: "select"},
    {id: "grade", label: "An", noWrap: true, sortable: true, width: "40px"},
    {id: "subcategory", label: "Subcategorie", noWrap: true, tooltip: true, width: "200px", tooltipTextLength: 21,},
    {id: "created_at", label: "Dată", noWrap: true, sortable: true, width: "90px", filter: true, filterType: "dateRange",},
    {id: "id", label: "ID", noWrap: true, sortable: true, numeric: true, width: "70px"},
]

const ItemsActive = () => {
    const {update, user} = useAuth();
    const navigate = useNavigate();
    const [subjectsFilterOptions, setSubjectsFilterOptions] = useState<any[]>([]);

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        tableColumns,
        setTableColumns,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        setPage,
        query,
        submittedQuery,
        currentFilters,
        orderBy,
        order,
        activeColumnIds,
        setActiveColumnIds,
        filterValues,
        startDate,
        endDate,
        handleFilterChange,
        handleResetFilters,
        handleApplyFilters,
        handleCancelFilters,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "created_at", undefined, "");

    const isTeacher = user.position === "teacher";

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const getItems = (currentPage = pageRef.current) => {
        APIGetCustomItems({
            page: `?page=${currentPage + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery,
            startDate: startDate,
            endDate: endDate,
            ...filterValues,
        })
            .then((response) => {
                if (response.data.success) {
                    const pageData = response.data.data;

                    if(pageData.data.length === 0 && currentPage > 0) {
                        const PreviousPage = currentPage - 1;

                        setPage(PreviousPage);
                        getItems();
                        return;
                    }

                    setTableData(pageData.data.map((item: any) => mapCustomItems(item)));
                    setTotalRows(pageData.total);
                    setPerPage(pageData.per_page);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(() => {
                // console.log("getTeachers ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => setLoading(false));
    }

    const mapCustomItems = useCallback((item: IItem) => {

        const actions = (
            <Box display="flex" justifyContent="end">
                <Box mr={1}>
                    <GenericButton
                        type="edit-small"
                        onClick={() => {
                            editItemHandler(item.id);
                        }}
                    />
                </Box>
                <Box>
                    <GenericButton type="archive" onClick={() => confirmArchive(item)}/>
                </Box>
            </Box>
        );

        return {
            id: item.id,
            title: <Link to={`/itemi-proprii/${item.id}`}>{item.title}</Link>,
            subject: item.subject,
            grade: item.grade,
            subcategory: item.subcategory_name,
            created_at: moment(item.created_at).locale("ro").format("DD.MM.YYYY"),
            ...(isTeacher && {actions: actions})
        }
    }, []);

    useEffect(() => {
        APIPostCustomItemsSubjects()
            .then((response) => {
                if (response.data.success) {
                    const items = response.data?.data?.data?.items ?? [];

                    setSubjectsFilterOptions(
                        items.map((item: any) => ({
                            label: item.name,
                            value: item.id,
                        })),
                    );
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(() => update("error", {show: true, code: "A001"}));
    }, []);

    useEffect(() => {
        setLoading(true);
        getItems();
    }, [page, order, orderBy, submittedQuery, currentFilters]);

    useEffect(() => {
        const columns = isTeacher ? ACTIVE_ITEMS_TEACHER : ACTIVE_ITEMS_PRINCIPAL;
        setTableColumns(columns);
    }, [user.position]);

    const editItemHandler = (id: number) => {
        navigate(`/itemi-proprii/${id}/editare`);
    };

    const archiveItem = (id: string, callback: Function) => {
        update("loading", {show: true, modal: true});

        APIArchiveCustomItem({id})
            .then((response) => {
                if (response.data.success) getItems(pageRef.current);
                    else update("error", {show: true, code: "A002"});

                callback();
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => update("loading", {show: false}));
    };

    const confirmArchive = (item: IItem) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat box-archive",
            title: "Ești sigur că vrei să arhivezi acest item?",
            message: (
                <>
                    Itemul <b>{item.title}</b> va fi arhivat. Veți regăsi acest item în
                    secțiunea <b>Itemi arhivați.</b>
                </>
            ),
            returnFunction: (callback: Function) => {
                archiveItem(item.id.toString(), callback);
            },
            buttonYes: "Arhivează",
            buttonNo: "Închide",
        });
    };

    const exportConfig = useMemo(() => ({
        buildRequestParams: () => ({
            page: `?page=${page + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery,
            startDate: startDate,
            endDate: endDate,
            export: true,
            ...filterValues,
        }),
        fetchData: async (params: any) => {
            const response = await APIGetCustomItems(params);
            if (!response.data.success || !response.data.data) {
                return [];
            }
            return response.data.data.export;
        },
        isExportingFromBackend: true
    }), [endDate, order, page, startDate, submittedQuery, filterValues]);

    return (
        <CustomDataTable
            loading={loading}
            columns={tableColumns}
            rows={tableData}
            rowsPerPage={perPage}
            page={page}
            totalRows={totalRows}
            onPageChange={handleChangePage}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasFilters={true}
            onChangeFilters={handleFilterChange}
            onCancelFilters={handleCancelFilters}
            onApplyFilters={handleApplyFilters}
            activeColumnIds={activeColumnIds}
            onActiveColumnIdsChange={setActiveColumnIds}
            resetFormData={handleResetFilters}
            filterValues={filterValues}
            hasExport={true}
            exportData={exportConfig}
            filterOptions={{subject: subjectsFilterOptions}}
            hasSearch={true}
            onSearch={handleSearch}
            onChange={handleSearchChange}
            searchValue={query}
            onClearSearch={handleClearSearch}
        />
    )
}

export default ItemsActive;